import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { toastError, toastSuccess } from '@components/Toast';
import CommonImage from '@components/CommonImage';

import useSingleFileUpload from '@hooks/useSingleFileUpload';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	addSupplierDocument,
	addSupplierSafetyDocument,
	getSupplierDocuments
} from '@services/supplierOnboardingService';

import { getFileType, getIconByFileType } from '@utils/Util';

import { documentTypes } from '@/constants/constant';

const SupplierDocumentUpload = ({
	title,
	fieldName,
	uploadType = '',
	multiple = true,
	callBack,
	onChange
}) => {
	const { id } = useParams();
	const { readFile } = useSingleFileUpload();
	const dispatch = useDispatch();
	const [uploadedFile, setUploadedFile] = useState(null);

	const renderTypeWiseDocuments = () => {
		const documentList = uploadedFile || [];
		if (documentList.length > 0) {
			return documentList?.map((item) => {
				let fileType;
				if (item.docUrl) {
					fileType = getFileType(item.docUrl);
				} else {
					fileType = getFileType(item.name);
				}

				return (
					<div>
						<div
							key={`document_${item.id}`}
							className="addition-info d-flex align-items-center mt-2"
						>
							<img className="file-icon" src={getIconByFileType(fileType)} width={20} alt="" />
							<p className="regular-12" onClick={() => window.open(item.docUrl, '_blank')}>
								{item.name}
							</p>
						</div>
					</div>
				);
			});
		}
	};

	const handleFileChange = async (e) => {
		try {
			const files = e.target.files;
			if (!files) {
				toastError('No files selected!');
				return;
			}
			const readFilePromises = Array.from(files).map((file) =>
				readFile(file).then((base64Str) => {
					let documentType = fieldName;
					if (['SUPPLIER_DOCUMENT', 'REGULATORY_DOCUMENT'].includes(uploadType)) {
						documentType = documentTypes[fieldName];
					} else if (uploadType === 'SAFETY_INFORMATION') {
						documentType = 'SUPPLIER_SAFETY_DOCUMENT';
					} else if (uploadType === 'SUPPORTING_DOCUMENT') {
						documentType = 'SUPPLIER_SUPPORTING_DOCUMENT';
					}
					return {
						name: file.name,
						docMimeType: file.type,
						documentType: documentType,
						base64Str
					};
				})
			);

			const data = await Promise.all(readFilePromises);
			setUploadedFile(data);
			if (uploadType === 'SUPPLIER_DOCUMENT') {
				dispatch(openLoader());
				await addSupplierDocument(id, data);
				await dispatch(getSupplierDocuments(id));
				dispatch(closeLoader());
				if (callBack) {
					await callBack();
				}
				toastSuccess('Updated successfully');
			}
			if (['SAFETY_INFORMATION'].includes(uploadType)) {
				dispatch(openLoader());
				await addSupplierSafetyDocument(id, { documentDTO: data[0], safetyDocumentType: fieldName });
				dispatch(closeLoader());
				if (callBack) {
					await callBack();
				}
				toastSuccess('Updated successfully');
			}
			if (['SUPPORTING_DOCUMENT', 'REGULATORY_DOCUMENT'].includes(uploadType)) {
				onChange(data[0], fieldName, 'documentDTO');
			}
		} catch {
			toastError('Failed to upload file!');
		}
	};

	return (
		<div className="add-file pb-3">
			<div className="d-flex justify-content-start align-items-center gap-3">
				<div className="inline-upload pr-12 w-50">
					<p className="regular-14 gray_dark_02 pb-2">{title}</p>
					<label htmlFor={fieldName}>
						<CommonImage src={`/icons/upload-dark.svg`} />
						Upload file
					</label>
					<input
						type="file"
						className="file-upload d-none"
						id={fieldName}
						name={fieldName}
						onClick={(e) => (e.target.value = null)}
						multiple={multiple}
						onChange={handleFileChange}
					/>
				</div>

				{!['SAFETY_INFORMATION', 'SUPPLIER_DOCUMENT'].includes(uploadType) && (
					<div className="expir-date w-50">
						<p className="regular-14 gray_dark_02 pb-2">Expiry Date</p>
						<input
							type="date"
							onChange={(e) => {
								onChange(e.target.value, fieldName, 'expiryDate');
							}}
						/>
					</div>
				)}
			</div>
			{renderTypeWiseDocuments()}
		</div>
	);
};

export default SupplierDocumentUpload;
